import { useCallback, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useShowCauseW_Selector } from '@givebacks/api/core';
import {
  type ConsumerPostCheckoutReceipt,
  showConsumerPostCheckoutQuery,
  showConsumerPostCheckoutReceiptQuery,
} from '@givebacks/api/store';
import { useQuery } from '@tanstack/react-query';
import { useUrlSearchParams } from 'core/Hooks';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Receipt, Stack } from 'ui';

import { useNavigateToAuth } from '../../../../hooks/useNavigateToAuth/useNavigateToAuth';
import { PostCheckoutRedirectPageTemplate } from '../../PostCheckoutRedirectPageTemplate/PostCheckoutRedirectPageTemplate';

const PURCHASES_ROUTE = '/account/purchases';

type PosCtaProps = {
  onPrimaryCtaClick: () => void;
  checkoutId?: string;
  cartId?: string;
  email?: string;
};

const POSReceiptBreakdown = ({ cart }: { cart: ConsumerPostCheckoutReceipt['carts'][0] }) => {
  const { data: cause } = useShowCauseW_Selector({
    uuid: cart?.cause?.uuid,
  });

  const website = useMemo(
    () => cause?.links?.find((link) => link.type == 'website')?.url,
    [cause?.links]
  );

  return (
    <>
      <Receipt.Breakdown
        imgUrl={cause?.logo?.url}
        taxId={cause?.tax_id}
        websiteUrl={website}
        causeName={cause?.name ?? ''}
        subtotal={cart.subtotal}
        fees={cart.processing_fees === 'absorb_fees' ? 0 : cart.fee}
        total={cart.total}
      />

      <Receipt.Cart>
        {cart.cart_items?.map((item) => (
          <Receipt.Item
            key={item.uuid}
            itemId={item.uuid}
            imgSrc={item.store_item?.images?.[0]?.url}
            name={item.store_item?.name}
            total={item.total}
            showQuantitySelector={false}
          />
        ))}
      </Receipt.Cart>
    </>
  );
};

const POSReceipt = ({ cart }: { cart: ConsumerPostCheckoutReceipt['carts'][0] }) => {
  return (
    <Receipt>
      <POSReceiptBreakdown cart={cart} />
    </Receipt>
  );
};

/**
 * Renders the POS receipts for the given checkout id or cart id.
 * Each cart is rendered as a separate receipt.
 */
const POSReceipts = ({ checkoutId, cartId }: { checkoutId?: string; cartId?: string }) => {
  const {
    data: checkout,
    isLoading: isCheckoutLoading,
    isLoadingError,
  } = useQuery(
    showConsumerPostCheckoutReceiptQuery({
      postCheckoutId: checkoutId,
      cartId,
    })
  );

  if (isCheckoutLoading) {
    return null;
  }
  // If the query fails or the checkout is not found, navigate to the home page
  if (isLoadingError || !checkout) {
    return <Navigate to="/home" />;
  }

  return (
    <Stack gap="lg">
      {checkout.carts.map((cart) => (
        <POSReceipt cart={cart} key={cart.uuid} />
      ))}
    </Stack>
  );
};

const PosCta = ({ onPrimaryCtaClick, checkoutId, email, cartId }: PosCtaProps) => {
  const navigate = useNavigate();
  const { data: checkoutCart, isLoading: isCartLoading } = useQuery(
    showConsumerPostCheckoutQuery({
      postCheckoutId: checkoutId,
      cartId,
    })
  );

  const handleViewTickets = useCallback(() => {
    const searchParams = new URLSearchParams();
    if (checkoutId) {
      searchParams.append('checkout_id', checkoutId);
    }
    if (email) {
      searchParams.append('email', email);
    }
    if (cartId) {
      searchParams.append('cart_id', cartId);
    }

    navigate(`/tickets?${searchParams.toString()}`);
  }, [cartId, checkoutId, email, navigate]);

  if (checkoutCart?.ticket) {
    return (
      <Button onClick={handleViewTickets} loading={isCartLoading}>
        View Tickets
      </Button>
    );
  }

  return (
    <Button onClick={onPrimaryCtaClick} loading={isCartLoading}>
      View All Purchases
    </Button>
  );
};

const POSReceiptLandingPage = () => {
  const { handleNavigateToAuth } = useNavigateToAuth();
  const { isAuthenticated } = useAuthentication();
  const navigate = useNavigate();

  /**
   * The POS receipt links from the api isn't using snake case for the search params,
   * so we need to support both camel case and snake case version of the search params.
   */
  const {
    email,
    checkout_id: checkoutId,
    // Allow both casings, prefer snake case
    cart_id,
    cartId,
  } = useUrlSearchParams(['cartId', 'email', 'checkout_id', 'cart_id']);

  const cartIdParam = useMemo(() => cart_id || cartId, [cart_id, cartId]);

  const onPrimaryCtaClick = useCallback(() => {
    if (isAuthenticated) {
      return navigate(PURCHASES_ROUTE);
    }

    return handleNavigateToAuth({ page: 'sign-in', redirect: PURCHASES_ROUTE });
  }, [isAuthenticated, handleNavigateToAuth, navigate]);

  return (
    <PostCheckoutRedirectPageTemplate
      title="View all your purchases in one place with the Givebacks app"
      leftSection={
        <PostCheckoutRedirectPageTemplate.AppBenefits>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            View all your receipts and payment history for all your organizations
          </PostCheckoutRedirectPageTemplate.AppBenefit>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            Manage donations and purchases for all your organizations
          </PostCheckoutRedirectPageTemplate.AppBenefit>
          <PostCheckoutRedirectPageTemplate.AppBenefit>
            Access fundraisers and online stores for all your organizations
          </PostCheckoutRedirectPageTemplate.AppBenefit>
        </PostCheckoutRedirectPageTemplate.AppBenefits>
      }
      rightSection={<POSReceipts checkoutId={checkoutId} cartId={cartIdParam} />}
      cta={
        <PosCta
          onPrimaryCtaClick={onPrimaryCtaClick}
          checkoutId={checkoutId}
          email={email}
          cartId={cartIdParam}
        />
      }
    />
  );
};

export { POSReceiptLandingPage };
